import { useEffect, useMemo, useState } from 'react'
import { UiBreadcrumb, UiCard, UiCardHeader, UiCardBody } from 'components/common'
import { Box, Button, Flex, Heading, useDisclosure } from '@chakra-ui/react'
import { getProjectById, getProjectQuoteById } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { useNavigate, useParams } from 'react-router-dom'
import { ProjectDetailGeneric } from './detail'
import { ProjectDetailOverview } from './overview'
import { ModalProjectCPM } from './cpm'
import { ModalProjectChangeOrderList } from './change-orders'
import { ProjectTasks } from './tasks'
import { ProjectContacts } from './contacts'
import { getAssetUrl } from 'lib'
import PropTypes from 'prop-types'
import { ModalNotes } from '../all-projects/ModalNotes'
import { format as formatDate } from 'date-fns'

export const PROJECT_DETAIL_TABS = {
  Detail: 'Detail',
  Overview: 'Overview',
  Tasks: 'Tasks',
  Contacts: 'Contacts',
  Notes: 'Notes'
}

export const ProjectDetail = ({ selectedTab }) => {
  const { projectId } = useParams()
  const { createToast } = useAppContext()
  const [project, setProject] = useState(null)
  const [quote, setQuote] = useState(null)
  const disclosureCPM = useDisclosure()
  const disclosureChangeOrders = useDisclosure()
  const disclosureNotes = useDisclosure()
  const navigate = useNavigate()

  const breadcrumbNav = useMemo(() => {
    return [
      {
        label: 'Projects',
        path: '/projects'
      },
      {
        label: project?.customerCompany || '',
        isCurrentPage: true
      }
    ]
  }, [project?.customerCompany])

  const loadProject = async () => {
    try {
      const project = await getProjectById(projectId)
      setProject({
        ...project,
        installDate: project?.installDate ? formatDate(new Date(new Date(project.installDate).getTime() + new Date(project.installDate).getTimezoneOffset() * 60000), 'yyyy-MM-dd') : null,
        productionClientEta: project?.productionClientEta ? formatDate(new Date(new Date(project.productionClientEta).getTime() + new Date(project.productionClientEta).getTimezoneOffset() * 60000), 'yyyy-MM-dd') : null
      })
      try {
        const quote = await getProjectQuoteById(projectId)
        setQuote(quote)
      } catch {
        setQuote(null)
      }
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
  }

  useEffect(() => {
    loadProject()
  }, [selectedTab])
  const handleClickTab = (tab) => {
    if (tab === PROJECT_DETAIL_TABS.Notes) {
      disclosureNotes.onOpen()
    } else {
      navigate(`/projects/detail/${projectId}/${tab.toLowerCase()}`)
    }
  }

  return (
    <>
      <UiBreadcrumb items={breadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Project Detail</Heading>
          <Flex gap={2}>
            <Button
              size="md"
              colorScheme="green"
              onClick={() => quote?.pdf && window.open(getAssetUrl(quote.pdf))}
            >
              View Quote PDF
            </Button>
            <Button
              size="md"
              colorScheme="orange"
              onClick={disclosureChangeOrders.onOpen}
            >
              Change Order
            </Button>
            <Button
              size="md"
              colorScheme="blue"
              px={8}
              onClick={disclosureCPM.onOpen}
            >
              CPM
            </Button>
          </Flex>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Flex gap={2}>
              {Object.keys(PROJECT_DETAIL_TABS).map(v =>
                <a
                  key={PROJECT_DETAIL_TABS[v]}
                  href={'/projects/detail/' + projectId + '/' + PROJECT_DETAIL_TABS[v]}
                  onClick={(e) => {
                    if (PROJECT_DETAIL_TABS[v] === PROJECT_DETAIL_TABS.Notes) {
                      e.preventDefault()
                    }
                  }}
                >
                  <Button
                    border={1}
                    borderColor="gray.200"
                    borderStyle="solid"
                    colorScheme={selectedTab === PROJECT_DETAIL_TABS[v] ? 'blue' : 'gray'}
                    size="xs"
                    borderRadius="4px"
                    onClick={() => handleClickTab(PROJECT_DETAIL_TABS[v])}
                  >
                    {PROJECT_DETAIL_TABS[v] === PROJECT_DETAIL_TABS.Notes ? `${PROJECT_DETAIL_TABS[v]} (${project?.notesCount || 0})` : PROJECT_DETAIL_TABS[v]}
                  </Button>
                </a>
              )}
            </Flex>
          </UiCardHeader>
          <UiCardBody p={8}>
            {selectedTab === PROJECT_DETAIL_TABS.Detail && (
              <ProjectDetailGeneric
                projectId={parseInt(projectId)}
                project={project}
                loadProject={loadProject}
              />
            )}
            {selectedTab === PROJECT_DETAIL_TABS.Overview && (
              <ProjectDetailOverview
                projectId={parseInt(projectId)}
                project={project}
              />
            )}
            {selectedTab === PROJECT_DETAIL_TABS.Tasks && (
              <ProjectTasks
                projectId={parseInt(projectId)}
              />
            )}
            {selectedTab === PROJECT_DETAIL_TABS.Contacts && (
              <ProjectContacts
                projectId={parseInt(projectId)}
              />
            )}
          </UiCardBody>
        </UiCard>
      </Box>
      <ModalNotes
        {...disclosureNotes}
        project={project}
        reload={() => {}}
      />
      <ModalProjectCPM
        projectId={parseInt(projectId)}
        {...disclosureCPM}
      />
      <ModalProjectChangeOrderList
        project={project}
        {...disclosureChangeOrders}
      />
    </>
  )
}

ProjectDetail.propTypes = {
  selectedTab: PropTypes.string
}
